/** Advisor Console (M14): portfolio across clients, and the firm's team and client seats. */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, Landmark, Lock, Plus, Stamp, UserMinus } from 'lucide-react';
import { api } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { relative, shortDate } from '../../lib/format';
import { Alert, Button, Card, EmptyState, Field, FilterBar, Modal, PageHeader, Skeleton, StatusChip, TableWrap, Td, Th, filterClass, inputClass } from '../../components/ui';
import { SecretReveal, errorText } from '../integrations/shared';

interface PortfolioClient {
  firmClientId: string;
  companyId: string;
  company: string;
  vatStatus: string | null;
  myRole: string | null;
  reconciliation: { status: 'OK' | 'BEHIND' | 'NO_BANK'; bankAccounts: number; oldestReconciledTo: string | null; unmatchedLines: number };
  close: { lastLocked: { name: string; endDate: string } | null; openPastPeriods: number };
  tax: { overdue: number; overdueForms: string[]; next: { label: string; periodLabel: string; dueDate: string; status: string }[]; quarterFiled: number; quarterTotal: number };
  exceptions: number;
  lastActivity: string | null;
}
interface Portfolio { firm: { name: string; code: string }; role: 'PARTNER' | 'STAFF'; year: number; quarter: number; clients: PortfolioClient[] }

export function AdvisorPortfolioPage(): JSX.Element {
  const { switchCompany } = useAuth();
  const navigate = useNavigate();
  const q = useQuery({ queryKey: ['firm-portfolio'], queryFn: () => api<Portfolio>('/firm/portfolio') });
  const [filter, setFilter] = useState<'all' | 'overdue' | 'recon' | 'close'>('all');
  const [search, setSearch] = useState('');
  const open = useMutation({ mutationFn: async (companyId: string) => { await switchCompany(companyId); navigate('/'); } });
  const rows = (q.data?.clients ?? []).filter((c) =>
    (!search || c.company.toLowerCase().includes(search.toLowerCase())) &&
    (filter === 'all' || (filter === 'overdue' && c.tax.overdue > 0) || (filter === 'recon' && c.reconciliation.status === 'BEHIND') || (filter === 'close' && c.close.openPastPeriods > 0)),
  );
  const totals = q.data ? { overdue: q.data.clients.filter((c) => c.tax.overdue).length, recon: q.data.clients.filter((c) => c.reconciliation.status === 'BEHIND').length, close: q.data.clients.filter((c) => c.close.openPastPeriods > 0).length } : null;
  return (
    <>
      <PageHeader title={q.data ? q.data.firm.name : 'Portfolio'} description={q.data ? `Q${q.data.quarter} ${q.data.year} · ${q.data.clients.length} client${q.data.clients.length === 1 ? '' : 's'} · firm code ${q.data.firm.code}` : 'Every client at a glance.'} />
      <FilterBar>
        <input className={filterClass} placeholder="Search clients" value={search} onChange={(e) => setSearch(e.target.value)} aria-label="Search clients" />
        <select className={filterClass} value={filter} onChange={(e) => setFilter(e.target.value as typeof filter)} aria-label="Show">
          <option value="all">All clients</option>
          <option value="overdue">BIR returns overdue{totals ? ` (${totals.overdue})` : ''}</option>
          <option value="recon">Reconciliation behind{totals ? ` (${totals.recon})` : ''}</option>
          <option value="close">Months not closed{totals ? ` (${totals.close})` : ''}</option>
        </select>
      </FilterBar>
      {q.isLoading && <Skeleton className="h-64 w-full" />}
      {q.error && <Alert tone="critical">{errorText(q.error)}</Alert>}
      {open.error && <div className="mb-4"><Alert tone="critical">{errorText(open.error)}</Alert></div>}
      {q.data && rows.length === 0 && (
        <Card><EmptyState title={q.data.clients.length ? 'No client matches' : 'No clients yet'} description={q.data.clients.length ? 'Change the filter above.' : `Ask each client's owner to connect your firm under Setup → Advisor Firms using code ${q.data.firm.code}.`} /></Card>
      )}
      {rows.length > 0 && (
        <Card>
          <TableWrap>
            <thead>
              <tr><Th>Client</Th><Th>BIR filing</Th><Th>Bank reconciliation</Th><Th>Month-end close</Th><Th>Activity</Th><Th> </Th></tr>
            </thead>
            <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
              {rows.map((c) => (
                <tr key={c.firmClientId}>
                  <Td>
                    <span className="font-semibold text-slate-900 dark:text-white">{c.company}</span>
                    <span className="block text-xs text-slate-500">{c.vatStatus === 'VAT' ? 'VAT-registered' : 'Non-VAT'}{c.myRole ? ` · you: ${c.myRole}` : ' · not assigned to you'}</span>
                  </Td>
                  <Td>
                    <div className="flex items-center gap-2">
                      <Stamp aria-hidden="true" className="h-4 w-4 text-slate-400" />
                      {c.tax.overdue > 0 ? <StatusChip tone="critical">{c.tax.overdue} overdue</StatusChip> : <StatusChip tone="ok">Up to date</StatusChip>}
                    </div>
                    <span className="mt-1 block text-xs text-slate-500">Q{q.data!.quarter}: {c.tax.quarterFiled} of {c.tax.quarterTotal} filed</span>
                    {c.tax.overdueForms.length > 0 && <span className="block max-w-xs text-xs text-red-700 dark:text-red-300">{c.tax.overdueForms.slice(0, 2).join(', ')}{c.tax.overdueForms.length > 2 ? '…' : ''}</span>}
                    {c.tax.next[0] && <span className="block text-xs text-slate-500">Next: {c.tax.next[0].label} {c.tax.next[0].periodLabel}, due {shortDate(c.tax.next[0].dueDate)}</span>}
                  </Td>
                  <Td>
                    <div className="flex items-center gap-2">
                      <Landmark aria-hidden="true" className="h-4 w-4 text-slate-400" />
                      {c.reconciliation.status === 'NO_BANK' ? <StatusChip tone="neutral">No bank accounts</StatusChip> : c.reconciliation.status === 'OK' ? <StatusChip tone="ok">Current</StatusChip> : <StatusChip tone="warning">Behind</StatusChip>}
                    </div>
                    {c.reconciliation.bankAccounts > 0 && <span className="mt-1 block text-xs text-slate-500">{c.reconciliation.oldestReconciledTo ? `All reconciled to ${shortDate(c.reconciliation.oldestReconciledTo)}` : 'Some accounts never reconciled'}{c.reconciliation.unmatchedLines ? ` · ${c.reconciliation.unmatchedLines} unmatched lines` : ''}</span>}
                  </Td>
                  <Td>
                    <div className="flex items-center gap-2">
                      <Lock aria-hidden="true" className="h-4 w-4 text-slate-400" />
                      {c.close.openPastPeriods > 0 ? <StatusChip tone="warning">{c.close.openPastPeriods} open</StatusChip> : <StatusChip tone="ok">Closed</StatusChip>}
                    </div>
                    <span className="mt-1 block text-xs text-slate-500">{c.close.lastLocked ? `Locked to ${c.close.lastLocked.name}` : 'No month locked yet'}</span>
                  </Td>
                  <Td className="text-sm text-slate-500">
                    {c.lastActivity ? relative(c.lastActivity) : 'No postings'}
                    {c.exceptions > 0 && <span className="block text-xs font-medium text-red-700 dark:text-red-300">{c.exceptions} integration exception{c.exceptions === 1 ? '' : 's'}</span>}
                  </Td>
                  <Td>{c.myRole && <Button size="sm" variant="secondary" busy={open.isPending && open.variables === c.companyId} onClick={() => open.mutate(c.companyId)}>Open <ArrowRight aria-hidden="true" className="h-4 w-4" /></Button>}</Td>
                </tr>
              ))}
            </tbody>
          </TableWrap>
        </Card>
      )}
    </>
  );
}

interface Member { id: string; userId: string; email: string; name: string; role: 'PARTNER' | 'STAFF'; active: boolean; lastLoginAt: string | null; mfaEnabled: boolean; clients: number }
interface FirmClient { id: string; companyId: string; company: string; status: string; allowedRoles: string[]; billing: string; connectedAt: string; staff: { membershipId: string; userId: string; name: string; email: string; role: string; active: boolean }[] }

export function AdvisorTeamPage(): JSX.Element {
  const { user } = useAuth();
  const qc = useQueryClient();
  const partner = user?.firm?.role === 'PARTNER';
  const members = useQuery({ queryKey: ['firm-members'], queryFn: () => api<Member[]>('/firm/members') });
  const clients = useQuery({ queryKey: ['firm-clients'], queryFn: () => api<FirmClient[]>('/firm/clients') });
  const [inviting, setInviting] = useState(false);
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [role, setRole] = useState<'STAFF' | 'PARTNER'>('STAFF');
  const [temp, setTemp] = useState<{ email: string; password: string } | null>(null);
  const [assigning, setAssigning] = useState<FirmClient | null>(null);
  const [who, setWho] = useState('');
  const [seatRole, setSeatRole] = useState('');
  const [message, setMessage] = useState<{ tone: 'ok' | 'critical'; text: string } | null>(null);
  const refresh = () => { void qc.invalidateQueries({ queryKey: ['firm-members'] }); void qc.invalidateQueries({ queryKey: ['firm-clients'] }); void qc.invalidateQueries({ queryKey: ['firm-portfolio'] }); };
  const invite = useMutation({
    mutationFn: () => api<{ email: string; temporaryPassword: string | null }>('/firm/members', { method: 'POST', body: { email, name, role } }),
    onSuccess: (r) => { refresh(); setInviting(false); setEmail(''); setName(''); if (r.temporaryPassword) setTemp({ email: r.email, password: r.temporaryPassword }); else setMessage({ tone: 'ok', text: `${r.email} already had a CoreX-lite login and was added to the firm.` }); },
  });
  const act = useMutation({
    mutationFn: ({ path, method = 'POST', body }: { path: string; method?: 'POST' | 'PATCH'; body?: unknown }) => api(path, { method, body }),
    onSuccess: () => { refresh(); setAssigning(null); setMessage(null); },
    onError: (e) => setMessage({ tone: 'critical', text: errorText(e) }),
  });
  const active = (members.data ?? []).filter((m) => m.active);
  return (
    <>
      <PageHeader title="Team & Clients" description="Put your staff on each client's books with the role that client allows. Access ends the moment either side ends it." actions={partner && <Button onClick={() => setInviting(true)}><Plus aria-hidden="true" className="h-4 w-4" /> Add team member</Button>} />
      {message && <div className="mb-4"><Alert tone={message.tone}>{message.text}</Alert></div>}
      <div className="grid gap-4 xl:grid-cols-5">
        <Card title="Clients and seats" className="xl:col-span-3">
          {clients.isLoading ? <Skeleton className="h-32 w-full" /> : clients.data?.length === 0 ? <EmptyState title="No clients connected" description="A client's owner connects your firm under Setup → Advisor Firms with your firm code." /> : (
            <ul className="divide-y divide-slate-100 dark:divide-slate-800">
              {clients.data?.map((c) => (
                <li key={c.id} className="py-3">
                  <div className="flex flex-wrap items-start justify-between gap-2">
                    <div>
                      <p className="font-semibold text-slate-900 dark:text-white">{c.company} {c.status !== 'ACTIVE' && <StatusChip tone="neutral">{c.status.toLowerCase()}</StatusChip>}</p>
                      <p className="text-xs text-slate-500">Connected {shortDate(c.connectedAt)} · roles allowed: {c.allowedRoles.join(', ')} · billed {c.billing === 'FIRM' ? 'to the firm' : 'to the client'}</p>
                    </div>
                    {partner && c.status === 'ACTIVE' && (
                      <div className="flex gap-2">
                        <Button size="sm" variant="secondary" onClick={() => { setAssigning(c); setWho(active[0]?.userId ?? ''); setSeatRole(c.allowedRoles[0] ?? ''); }}><Plus aria-hidden="true" className="h-4 w-4" /> Assign</Button>
                        <Button size="sm" variant="ghost" onClick={() => { if (window.confirm(`Stop working on ${c.company}? Every seat your firm holds there ends now.`)) act.mutate({ path: `/firm/clients/${c.id}/leave` }); }}>Leave</Button>
                      </div>
                    )}
                  </div>
                  {c.staff.length > 0 && (
                    <ul className="mt-2 space-y-1">
                      {c.staff.map((s) => (
                        <li key={s.membershipId} className={`flex items-center justify-between rounded-lg bg-slate-50 px-3 py-1.5 text-sm dark:bg-slate-800/60 ${s.active ? '' : 'opacity-50'}`}>
                          <span>{s.name} <span className="text-xs text-slate-500">{s.email}</span></span>
                          <span className="flex items-center gap-2">
                            <StatusChip tone={s.active ? 'info' : 'neutral'}>{s.role}{s.active ? '' : ' · ended'}</StatusChip>
                            {partner && s.active && <Button size="sm" variant="ghost" aria-label={`End ${s.name}'s seat`} onClick={() => act.mutate({ path: `/firm/assignments/${s.membershipId}/end` })}><UserMinus aria-hidden="true" className="h-4 w-4" /></Button>}
                          </span>
                        </li>
                      ))}
                    </ul>
                  )}
                </li>
              ))}
            </ul>
          )}
        </Card>
        <Card title="Team" className="xl:col-span-2">
          {members.isLoading ? <Skeleton className="h-32 w-full" /> : (
            <TableWrap>
              <thead><tr><Th>Member</Th><Th>Role</Th><Th align="right">Clients</Th><Th> </Th></tr></thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                {members.data?.map((m) => (
                  <tr key={m.id} className={m.active ? '' : 'opacity-50'}>
                    <Td><span className="font-medium">{m.name}</span><span className="block text-xs text-slate-500">{m.email}{m.lastLoginAt ? ` · seen ${relative(m.lastLoginAt)}` : ' · never signed in'}</span></Td>
                    <Td>{partner && m.active && m.userId !== user?.id ? (
                      <select className={filterClass} value={m.role} onChange={(e) => act.mutate({ path: `/firm/members/${m.id}`, method: 'PATCH', body: { role: e.target.value } })} aria-label={`Role of ${m.name}`}><option value="STAFF">Staff</option><option value="PARTNER">Partner</option></select>
                    ) : <StatusChip tone={m.role === 'PARTNER' ? 'info' : 'neutral'}>{m.role === 'PARTNER' ? 'Partner' : 'Staff'}</StatusChip>}</Td>
                    <Td align="right" className="tabular-nums">{m.clients}</Td>
                    <Td>{partner && m.userId !== user?.id && <Button size="sm" variant="ghost" onClick={() => act.mutate({ path: `/firm/members/${m.id}`, method: 'PATCH', body: { active: !m.active } })}>{m.active ? 'Remove' : 'Restore'}</Button>}</Td>
                  </tr>
                ))}
              </tbody>
            </TableWrap>
          )}
        </Card>
      </div>

      {inviting && (
        <Modal title="Add a team member" description="They sign in with their own email and must turn on two-step sign-in." onClose={() => setInviting(false)}>
          <form className="space-y-4" onSubmit={(e) => { e.preventDefault(); invite.mutate(); }}>
            {invite.error && <Alert tone="critical">{errorText(invite.error)}</Alert>}
            <Field label="Name" required><input className={inputClass} value={name} onChange={(e) => setName(e.target.value)} required minLength={2} /></Field>
            <Field label="Email" required><input className={inputClass} type="email" value={email} onChange={(e) => setEmail(e.target.value)} required /></Field>
            <Field label="Firm role" hint="Partners manage the team and client seats.">
              <select className={inputClass} value={role} onChange={(e) => setRole(e.target.value as 'STAFF' | 'PARTNER')}><option value="STAFF">Staff</option><option value="PARTNER">Partner</option></select>
            </Field>
            <div className="flex justify-end gap-2"><Button type="button" variant="secondary" onClick={() => setInviting(false)}>Cancel</Button><Button type="submit" busy={invite.isPending}>Add</Button></div>
          </form>
        </Modal>
      )}
      {assigning && (
        <Modal title={`Assign to ${assigning.company}`} description="The seat appears in their company switcher at once." onClose={() => setAssigning(null)}>
          <form className="space-y-4" onSubmit={(e) => { e.preventDefault(); act.mutate({ path: '/firm/assignments', body: { firmClientId: assigning.id, userId: who, role: seatRole } }); }}>
            {message?.tone === 'critical' && <Alert tone="critical">{message.text}</Alert>}
            <Field label="Team member" required>
              <select className={inputClass} value={who} onChange={(e) => setWho(e.target.value)} required>{active.map((m) => <option key={m.userId} value={m.userId}>{m.name} ({m.email})</option>)}</select>
            </Field>
            <Field label="Role on this client" required hint="Only the roles this client allows your firm.">
              <select className={inputClass} value={seatRole} onChange={(e) => setSeatRole(e.target.value)} required>{assigning.allowedRoles.map((r) => <option key={r} value={r}>{r}</option>)}</select>
            </Field>
            <div className="flex justify-end gap-2"><Button type="button" variant="secondary" onClick={() => setAssigning(null)}>Cancel</Button><Button type="submit" busy={act.isPending}>Assign</Button></div>
          </form>
        </Modal>
      )}
      {temp && <SecretReveal title="Team member added" items={[{ label: 'Email', value: temp.email }, { label: 'Temporary password', value: temp.password }]} onClose={() => setTemp(null)} />}
    </>
  );
}
